import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { attachmentsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Paperclip, X, Download, Trash2, FileText, Music, Video, Image as ImageIcon, File, Loader2 } from 'lucide-react';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return ImageIcon;
  if (contentType?.startsWith('audio/')) return Music;
  if (contentType?.startsWith('video/')) return Video;
  if (contentType?.includes('pdf') || contentType?.includes('document') || contentType?.includes('text')) return FileText;
  return File;
}

const AttachmentBar = forwardRef(function AttachmentBar({ noteId, onImageInsert }, ref) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadQueue, setUploadQueue] = useState(0);
  const [uploadDone, setUploadDone] = useState(0);
  const fileInputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    click: () => fileInputRef.current?.click(),
    uploadFile: (file) => uploadFile(file),
    uploadFiles: (files) => uploadFiles(files),
  }));

  const fetchAttachments = useCallback(async () => {
    if (!noteId) return;
    try {
      const res = await attachmentsAPI.list(noteId);
      setAttachments(res.data);
    } catch {
      // Silently handle — no attachments yet
    }
  }, [noteId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const uploadFile = async (file) => {
    if (!noteId || !file) return;
    setUploading(true);
    setUploadFileName(file.name);
    setUploadProgress(0);

    try {
      let result;
      if (file.size > CHUNK_SIZE) {
        // Chunked upload for large files
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const initRes = await attachmentsAPI.initChunked(noteId, file.name, file.size, totalChunks);
        const uploadId = initRes.data.upload_id;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          await attachmentsAPI.uploadChunk(uploadId, i, chunk);
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 95));
        }

        const completeRes = await attachmentsAPI.completeChunked(uploadId);
        result = completeRes.data;
      } else {
        // Direct upload for small files
        const res = await attachmentsAPI.upload(noteId, file, (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 95));
        });
        result = res.data;
      }

      setUploadProgress(100);
      toast.success(`Uploaded ${file.name}`);

      // If it's an image, offer to insert inline
      if (result.is_image && onImageInsert) {
        const url = attachmentsAPI.getFileUrl(result.id);
        onImageInsert(url, result.original_filename);
      }

      await fetchAttachments();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName('');
    }
  };

  const uploadFiles = async (files) => {
    if (!noteId || !files || files.length === 0) return;
    const fileList = Array.from(files);
    setUploadQueue(fileList.length);
    setUploadDone(0);
    setUploading(true);

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      setUploadFileName(file.name);
      setUploadDone(i);
      setUploadProgress(0);

      try {
        let result;
        if (file.size > CHUNK_SIZE) {
          const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
          const initRes = await attachmentsAPI.initChunked(noteId, file.name, file.size, totalChunks);
          const uploadId = initRes.data.upload_id;
          for (let c = 0; c < totalChunks; c++) {
            const start = c * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);
            await attachmentsAPI.uploadChunk(uploadId, c, chunk);
            setUploadProgress(Math.round(((c + 1) / totalChunks) * 95));
          }
          const completeRes = await attachmentsAPI.completeChunked(uploadId);
          result = completeRes.data;
        } else {
          const res = await attachmentsAPI.upload(noteId, file, (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 95));
          });
          result = res.data;
        }
        setUploadProgress(100);
        if (result.is_image && onImageInsert) {
          onImageInsert(attachmentsAPI.getFileUrl(result.id), result.original_filename);
        }
      } catch (err) {
        const msg = err.response?.data?.detail || `Failed to upload ${file.name}`;
        toast.error(msg);
      }
    }

    setUploadDone(fileList.length);
    toast.success(`Uploaded ${fileList.length} file${fileList.length > 1 ? 's' : ''}`);
    await fetchAttachments();
    setUploading(false);
    setUploadProgress(0);
    setUploadFileName('');
    setUploadQueue(0);
    setUploadDone(0);
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (files.length === 1) {
        uploadFile(files[0]);
      } else {
        uploadFiles(files);
      }
    }
    e.target.value = '';
  };

  const handleDelete = async (attachmentId, filename) => {
    try {
      await attachmentsAPI.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
      toast.success(`Removed ${filename}`);
    } catch {
      toast.error('Failed to remove attachment');
    }
  };

  const handleDownload = (attachment) => {
    const url = attachmentsAPI.getFileUrl(attachment.id);
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.original_filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!noteId) return null;

  const hasAttachments = attachments.length > 0;
  const images = attachments.filter((a) => a.is_image);
  const files = attachments.filter((a) => !a.is_image);

  return (
    <div data-testid="attachment-bar">
      {/* Upload button + progress */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        data-testid="attachment-file-input"
      />

      {uploading && (
        <div className="mx-4 sm:mx-8 lg:mx-16 mb-3 p-3 rounded-lg border border-border/50 bg-secondary/30" data-testid="attachment-upload-progress">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="truncate flex-1">
              {uploadQueue > 1
                ? `Uploading ${uploadFileName} (${uploadDone + 1}/${uploadQueue})...`
                : `Uploading ${uploadFileName}...`}
            </span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-accent-amber rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Attachment gallery */}
      {hasAttachments && (
        <div className="mx-4 sm:mx-8 lg:mx-16 mb-3" data-testid="attachment-gallery">
          {/* Image thumbnails */}
          {images.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-2">
                {images.map((att) => (
                  <div
                    key={att.id}
                    className="group relative w-24 h-24 rounded-lg border border-border/50 overflow-hidden bg-muted/30"
                    data-testid={`attachment-image-${att.id}`}
                  >
                    <img
                      src={attachmentsAPI.getFileUrl(att.id)}
                      alt={att.original_filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                      {onImageInsert && (
                        <button
                          onClick={() => onImageInsert(attachmentsAPI.getFileUrl(att.id), att.original_filename)}
                          className="p-1.5 rounded-md bg-white/20 hover:bg-white/30 text-white"
                          title="Insert inline"
                          data-testid={`attachment-insert-${att.id}`}
                        >
                          <ImageIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(att)}
                        className="p-1.5 rounded-md bg-white/20 hover:bg-white/30 text-white"
                        title="Download"
                        data-testid={`attachment-download-${att.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(att.id, att.original_filename)}
                        className="p-1.5 rounded-md bg-red-500/40 hover:bg-red-500/60 text-white"
                        title="Remove"
                        data-testid={`attachment-delete-${att.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-black/50 text-[10px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">
                      {att.original_filename}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Non-image file cards */}
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((att) => {
                const Icon = getFileIcon(att.content_type);
                return (
                  <div
                    key={att.id}
                    className="group flex items-center gap-3 px-3 py-2 rounded-lg border border-border/40 bg-secondary/20 hover:bg-secondary/40 transition-colors"
                    data-testid={`attachment-file-${att.id}`}
                  >
                    <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">{att.original_filename}</p>
                      <p className="text-[10px] text-muted-foreground">{formatFileSize(att.size)}</p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleDownload(att)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        title="Download"
                        data-testid={`attachment-download-${att.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(att.id, att.original_filename)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                        title="Remove"
                        data-testid={`attachment-delete-${att.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default AttachmentBar;
